// English message bundle. Mirrors the key structure of fi.js exactly —
// scripts/audit/i18n-keys.cjs enforces parity in both directions.
export const en = {
    common: {
        loading: "Loading…",
        languageToggle: "English — vaihda suomeksi",
        skipToContent: "Skip to content",
        dialog: {
            close: "Close",
        },
        footer: {
            terms: "Terms of Service",
            calculator: "Concrete calculator",
            pumpingPrice: "Concrete pumping price",
            coverageAreas: "Coverage areas",
            game: "Concrete game",
            privacy: "Privacy Policy",
            cookieSettings: "Cookie settings",
            contact: "Contact",
        },
        nav: {
            joinCta: "Join Jerry",
            myRequests: "Quote requests",
            requestQuoteCta: "Request a quote",
            defaultUserName: "User",
            myDetails: "My details",
            logout: "Log out",
            impersonateAction: "Log in as…",
            switchCompany: "Switch company",
            myOrders: "My pours",
            support: "Support",
            admin: "Management",
            // Public main-nav link in App.jsx's Toolbar — a static HTML page
            // generated into dist/, distinct from the footer's coverageAreas.
            coverageAreas: "Coverage areas",
        },
        auth: {
            switchCompanyFailed: "Failed to switch company.",
            impersonateFailed: "Impersonation failed.",
            extendSessionFailed: "Failed to extend the session.",
            appleNotConfigured: "Apple sign-in is not configured.",
            appleLoadFailed: "Failed to load Apple sign-in.",
            linkedinNotConfigured: "LinkedIn sign-in is not configured.",
        },
        subscribePush: {
            promptLabel: "Subscribe to browser notifications",
            successLabel: "Notifications on",
            iosNeedsPwaHint: "Add Betonijerry to your home screen to get notifications on your phone.",
            deniedHint: "Browser notifications are blocked. You can allow them in your browser settings.",
            subscribing: "Subscribing…",
        },
        fcm: {
            signInRequired: "Sign in to subscribe to notifications.",
            subscribeFailed: "Failed to subscribe to notifications.",
            registerDeviceFailed: "Failed to register the device.",
        },
        addressAutocomplete: {
            label: "Street address",
            noSuggestions: "No suggestions",
            typeFreely: "You can enter the address manually",
        },
        pageMeta: {
            defaultTitle: "Concrete Pumping in Finland | Request a Quote | BetoniJerry",
            defaultDescription:
                "Submit a free quote request for concrete pumping in almost all of Finland — no need to call around. One request reaches the pumping companies in your area, and you'll get offers quickly — compare prices and choose yourself. No commitment required.",
        },
        coverageSentence: {
            fallback:
                "Pumping companies in the Helsinki metropolitan area, Tampere, Jyväskylä, Lahti, and elsewhere in Southern, Southeastern, and Eastern Finland.",
        },
    },
    errors: {
        tooManyAttempts: "Too many attempts. Please try again in a moment.",
        addressCheckRateLimited: "Too many address checks. Please try again in a moment.",
        requestAlreadySent: "This request has already been sent.",
        requestNotEditable: "This request can no longer be edited.",
        requestHasOffers: "This request can't be cancelled — it has already received offers.",
        requestNotFound: "Request not found.",
        noAccountAccess: "You don't have permission to order on this account.",
        accountNotFound: "There is no account with this email address.",
        loginSessionInvalid: "The code has expired. Please request a new one.",
        loginCodeInvalid: "Wrong code. Check the code in your email and try again.",
        generic: "Something went wrong. Please try again.",
        networkError: "network error",
    },
    auth: {
        checkFailed: "Check failed.",
        loginFailed: "Login failed.",
        codeSendFailed: "Failed to send the code.",
        codeLoginFailed: "Login with code failed.",
        resetCodeSendFailed: "Failed to send the reset code.",
        passwordChangeFailed: "Failed to change the password.",
        passwordChangedLoginFailed: "Your password was changed successfully, but sign-in failed ({reason}). Open the \"Password\" tab and sign in with your new password.",
        passwordSetLoginFailed: "Your password was set successfully, but sign-in failed ({reason}). Refresh the page and sign in with your new password.",
        registerCodeSendFailed: "Failed to send the verification code.",
        verifyCodeInvalid: "Invalid verification code.",
        registerFailed: "Failed to create the account.",
        googleLoginFailed: "Google sign-in failed.",
        microsoftLoginFailed: "Microsoft sign-in failed.",
        appleLoginFailed: "Apple sign-in failed.",
        linkedinLoginFailed: "LinkedIn sign-in failed.",

        shared: {
            cancel: "Cancel",
            save: "Save",
            saving: "Saving…",
            saveFailed: "Save failed.",
            loginButton: "Log in",
            loggingIn: "Logging in…",
            emailLabel: "Email",
            etunimiLabel: "First name",
            etunimiRequired: "First name required",
            sukunimiLabel: "Last name",
            sukunimiRequired: "Last name required",
            puhelinLabel: "Phone",
            puhelinRequired: "Phone number required",
            passwordLabel: "Password",
            min8Chars: "At least 8 characters",
            verificationCodeLabel: "Verification code",
            companyNameLabel: "Company name",
            companyNameRequired: "Company name required",
            ytunnusLabel: "Business ID",
            streetAddressLabel: "Street address",
            postalCodeLabel: "Postal code",
            cityLabel: "City",
            removeFavorite: "Remove from favorites",
            addFavorite: "Save to favorites",
        },

        login: {
            title: "Log in",
            emailPrefix: "Email:",
            changeEmail: "Change",
            emailCodeMethod: "Email code",
            forgotPasswordMethod: "Forgot your password?",
            setPasswordMethod: "Set a password",
            codeRequired: "Enter the login code",
            resetCodeRequired: "Enter the reset code",
            passwordMinLength: "Your password must be at least 8 characters.",
            sendCodeIntro: "We'll send a 4-digit login code to {email}.",
            sending: "Sending…",
            sendCodeButton: "Send code",
            codeSentTo: "Code sent to {email}. Enter the 4-digit code below.",
            codeFieldLabel: "Login code",
            loginWithCodeButton: "Log in with code",
            resendCode: "Send a new code",
            resetIntroWithPassword: "We'll send a reset code to {email}. After that you can set a new password.",
            resetIntroSetPassword: "We'll send a verification code to {email}. After that you can set a password for your account.",
            sendResetCodeButton: "Send reset code",
            sendVerifyCodeButton: "Send verification code",
            resetVerifyIntroWithPassword: "Enter the reset code and a new password.",
            resetVerifyIntroSetPassword: "Enter the verification code and choose a password.",
            resetCodeLabel: "Reset code",
            newPasswordLabel: "New password",
            changingPassword: "Changing…",
            settingPassword: "Setting…",
            changePasswordAndLogin: "Change password and log in",
            setPasswordAndLogin: "Set password and log in",
            resendResetCode: "Send a new reset code",
            resendVerifyCode: "Send a new verification code",
        },

        register: {
            stepCode: "Code",
            stepProfile: "Profile",
            stepIndicator: "Step {current} / {total} · {label}",
            verifyCodeRequired: "Enter the verification code",
            ytunnusRequired: "Business ID required",
            legalRequired: "You must accept the terms of service",
            checkEmailTitle: "Check your email",
            sendingCodeIntro: "We're sending a verification code to {email}…",
            codeSentIntro: "We sent a 4-digit verification code to {email}.",
            verifying: "Verifying…",
            verifyCodeButton: "Verify code",
            useDifferentEmail: "Use a different email",
            profileTitle: "Complete your account",
            isCompanyLabel: "I'm a company",
            legalConsent: "I accept {terms}. See also {privacy}.",
            termsLinkText: "the terms of service",
            privacyLinkText: "the privacy policy",
            creatingAccount: "Creating account…",
            createAccountButton: "Create account",
        },

        social: {
            microsoftInProgress: "A previous Microsoft sign-in is still in progress. Please try again in a moment.",
            appleNoToken: "No ID token received from Apple",
            orContinueWith: "Or continue with",
            continueWithMicrosoft: "Continue with Microsoft",
            continueWithApple: "Continue with Apple",
            continueWithLinkedin: "Continue with LinkedIn",
            legalConsent: "By continuing you accept {terms}. See also {privacy}.",
            termsLinkText: "the terms of service",
            privacyLinkText: "the privacy policy",
        },

        company: {
            editTitle: "Edit company",
            offersWillGoTo: "Quote requests will be sent to: {email}",
            noEmailWarning: "This person has no email address — quote requests will go to the billing email or the admins",
            nameTooLong: "Name is too long (max 250 characters)",
            ytunnusFormat: "Enter the Business ID in the format 1234567-8",
            ytunnusConflict: "This Business ID is already in use by another company",
            billingAddressLabel: "Billing address",
            introSectionTitle: "About",
            introHelperText: "This text is shown to the customer alongside the offer.",
            settingsLoadFailed: "Failed to load the about text and Jerry settings. You can still save the basic details.",
            introPlaceholder: "Briefly describe your company: equipment, experience, service area…",
            publicListingTitle: "Public company profile",
            publicListingHelper: "You can publish your company on betonijerry.fi's public pages, so customers can also find you through search engines.",
            websiteLabel: "Website",
            // LEGAL ARTIFACT (draft translation) — NOT YET LEGALLY REVIEWED.
            // This is a second legal artifact, not a routine translation of the
            // Finnish consent text: a human must sign it off before this English
            // value is relied on for an English-language provider. See
            // merge-betonijerry-report.md for the flagged review item.
            publicListingConsent: "Publish my company on betonijerry.fi's public pages. Published information: name, Business ID, description, website address, logo and service area. The contact person's name, phone number and email address will not be published.",
            uploadLogo: "Upload logo",
            logoUploaded: "Logo uploaded ✓",
            logoUploadFailed: "Failed to upload the logo",
            publicUrlCaption: "Public address: {link} (published with the next update)",
            jerrySettingsTitle: "Jerry settings",
            jerrySettingsHelper: "Select a person for Jerry offers — new quote requests will be sent to that email address.",
            contactPersonLabel: "Contact person for Jerry offers",
            noneSelected: "None selected",
            personFallback: "Person {id}",
            noEmailAddress: "no email address",
            offerEmailLabel: "Email address for quote requests (optional)",
            offerEmailHelper: "May be a shared mailbox, e.g. tarjoukset@company.fi. Signing in still always uses a personal email address.",
            offerEmailInvalid: "Check the email address (e.g. tarjoukset@company.fi)",
            offersGoToOverride: "Quote requests are sent to the address below — this person signs in and responds to them",
        },

        person: {
            emailRequired: "Email required",
            emailInvalid: "Invalid email address",
            emailConflict: "This email address is already in use by another account",
            editTitle: "Edit contact details",
            billingAddressLabel: "Billing address (as a private individual)",
            useSiteAddressLabel: "Same as the site address",
        },

        impersonate: {
            forbidden: "Not authorized to impersonate this user.",
            notFound: "Target person not found. Check the email address.",
            genericFailed: "Impersonation failed.",
            dialogTitle: "Log in as a user",
            savedLabel: "Saved",
            emailLabel: "User's email",
        },

        impersonateBanner: {
            unknownUser: "user",
            statusLine: "Impersonating: {name} · {time}",
            extendButton: "Extend 10 min",
            stopButton: "Stop",
        },

        account: {
            menuAriaLabel: "User menu",
            companyPrefix: "Company: {company}",
        },

        linkedin: {
            backToHome: "Back to home",
            finishing: "Finishing LinkedIn sign-in…",
        },

        email: {
            title: "Log in or create an account",
            intro: "Enter your email address — we'll check whether you already have an account.",
            invalidEmail: "Enter a valid email address",
            checking: "Checking…",
            continueButton: "Continue",
        },
    },
    offers: {
        saveFailed: "Failed to save the offer.",
        sendFailed: "Failed to send.",
        acceptFailed: "Failed to accept the offer.",
    },
    wizard: {
        privateBillingCreateFailed: "Failed to create private billing details.",
        sendFailed: "Failed to send.",

        shared: {
            rasitusluokkaLabel: "Exposure class",
            suunnitteluikaLabel: "Design life",
            years50: "50 years",
            years100: "100 years",
            kayttokohdeLabel: "Application",
            cancel: "Cancel",
            startOver: "Start over",
            example: "E.g.",
        },

        units: {
            pumppufirmaOne: "pumping company",
            pumppufirmaOther: "pumping companies",
        },

        validation: {
            addressRequired: "Enter the site address",
            addressNotFound: "Check the spelling of the address — we couldn't find it on the map",
            amountRequired: "Enter the concrete amount (m³)",
            amountMin: "The minimum amount is 1 m³",
            amountStep: "Enter the amount in 0.5 m³ steps (e.g. 1, 1.5, 2)",
            kayttokohdeRequired: "Select the application",
            raekokoConflict: "Line pumping — use fine 16 mm or 8 mm aggregate",
            pumpBothZero: "Enter either the boom or line length — both can't be 0",
            termsRequired: "Accept the terms to send the quote request",
            laskutusRequired: "Select who to bill — a company or private individual.",
        },

        stepAddress: {
            title: "Site address",
            jerryMessage: "Let's start with the site address — I'll check at the same time whether there's a pumping company in your area. Free and non-binding; sending requires a free account at the end.",
            intro: "Enter the site's street address and pick the right one from the suggestions.",
            addressLabel: "Address",
            addressHelper: "E.g. Mannerheimintie 12, 00100 Helsinki",
        },

        addressFeasibility: {
            checking: "Checking the address…",
            errorTitle: "Check failed",
            errorFallback: "Unknown error — try again in a moment.",
            retry: "Try again",
            notFoundTitle: "We couldn't find this address",
            notFoundBody: "Check the spelling — try adding the postal code, for example.",
            noSupplyTitle: "Not yet in this area",
            noSupplyBody: "You can still submit a quote request — we'll email you when the service expands to your area.",
            nearestVarikko: "{km} km to the nearest depot",
            successTitle: "Pumping companies in this area",
            successBody: "{count} {unit} in the area.",
        },

        stepPump: {
            title: "Pump",
            heading: "How far is the pump from the pour site?",
            intro: "The pump truck sets up its {boom} as close as possible, and if needed the boom tip is extended further with a {linja} — a longer pipe or hose. Give both distances so the pumping company knows what equipment the site needs.",
            boomWord: "boom",
            linjaWord: "line",
            boomSectionTitle: "Boom length",
            boomFieldLabel: "Minimum boom length",
            boomAriaLabel: "Boom length in meters",
            boomHelper: "The smallest boom class that reaches the farthest pour point from the pump truck's setup spot.",
            noBoomLabel: "No boom — pumped straight from the back of the truck",
            boomResultTitle: "Pumping companies with this boom",
            boomResultBody: "{count} {unit} can supply this boom length.",
            boomNoneTitle: "No boom this long in the area",
            boomNoneBody: "Pumping companies in the area don't have a boom this long — you can still send the quote request.",
            linjaLabel: "Line length",
            linjaAriaLabel: "Line length in meters",
            linjaHelper: "The length of a separate extension hose or pipeline from the pump or boom tip to the farthest pour point.",
            noLinjaLabel: "No extension line — concrete drops straight from the boom tip",
            roikostaSuffix: "(hanging)",
            bothEmptyNote: "Don't know the distances? You can leave the fields blank — the pumping company will assess what's needed when quoting.",
            totalLabel: "Total approx.",
        },

        stepConcrete: {
            title: "Concrete",
            calculatorLoadFailed: "The calculator failed to load. Refresh the page and try again.",
            amountSectionTitle: "Amount and duration",
            amountPlaceholder: "e.g. 10",
            amountAriaLabel: "Concrete amount in cubic meters",
            amountHelper: "Amount to order — min. 1 m³, in 0.5 m³ steps",
            kestoPlaceholder: "e.g. 2",
            kestoAriaLabel: "Estimated pumping time in hours",
            kestoHelper: "Estimated work time",
            editFloorPlan: "Edit floor plan (calculator)",
            drawFloorPlan: "Draw a floor plan and calculate the amount",
            leftoverBoth: "From the boom and line",
            leftoverBoom: "From the boom",
            leftoverLinja: "From the line",
            leftoverBody: "{lead}, about {amount} of concrete stays unpumped. Order in 0.5 m³ steps. Leftover concrete stays on-site or is taken for recycling for a fee.",
            leftoverAmount: "{litres} l ({m3} m³)",
            lujuusLabel: "Strength class",
            raekokoLabel: "Aggregate size",
            notkeusLabel: "Consistency",
            helpMeChoose: "Help me choose",
            or: "— or —",
            iKnowExact: "I know the exact concrete spec",
            guidedResultTitle: "Assistant result",
            reopenGuide: "Reopen the assistant",
            switchToManual: "Switch to manual entry",
            descriptionLabel: "Description of the pour and any special requirements",
            descriptionHelperMuu: "Specify your 'Other' selections here (e.g. strength class, aggregate size, consistency, exposure class)",
            descriptionHelper: "What are you pouring? Also mention any special concrete requirements (e.g. frost resistance, fibers, white cement) and other needs.",
            useGuideAnyway: "Use the assistant after all",
            calculatorTitle: "Concrete calculator",
            useAmount: "Use amount",
        },

        stepReview: {
            title: "Review your quote request",
            sectionAddress: "Address",
            addressLabel: "Site address",
            noSupplyWarning: "No pumping company in this area yet — the request will be registered to wait for coverage.",
            boomFromRear: "Pumped from the rear",
            providerEstimates: "Pumping company will assess",
            linjaFromTip: "Hanging (straight from the boom tip)",
            guidedBadge: "Determined with the assistant",
            amountLabel: "Amount",
            kestoLabel: "Estimated pumping time",
            lujuusLabel: "Strength",
            descriptionLabel: "Description",
            attachmentsLabel: "Attachments",
            attachmentsCount: "{n} file(s)",
            noDetails: "No additional details",
            sectionContact: "Contact details",
            nameLabel: "Name",
            phoneLabel: "Phone",
            emailLabel: "Email",
            notAuthenticated: "You're not signed in. The request can't be sent without contact details — sign in or create an account below.",
            readMore: "Read more",
        },

        stepDetails: {
            title: "Additional details",
            driveLabel: "Directions",
            driveHelper: "E.g. gate code, narrow access road, location of the site gate. Don't enter contact details here — those are asked separately.",
            otherLabel: "Other details",
            otherHelper: "Anything else the pumping company should know. Don't enter contact details here — those are asked separately.",
            attachmentsAuthNote: "You can add attachments (images, PDF) once you're signed in — even after sending the request.",
        },

        quoteStepper: {
            stepAriaLabel: "Step {current} of {total}",
        },

        navButtons: {
            next: "Next →",
            back: "← Back",
            send: "Send quote request",
            sending: "Sending…",
            needAccount: "You need an account to send — it's free.",
            alreadyHaveAccount: "Already have an account? Sign in",
        },

        resumeNotice: {
            title: "Continuing an unfinished quote request",
            startedAt: "started {when}",
            confirmPrompt: "Delete it?",
            hideAriaLabel: "Hide notice",
        },

        quoteWizard: {
            checkAddressNext: "Check address →",
            autosaveAuth: "Your quote request saves automatically — you can continue later from where you left off.",
            autosaveAnon: "The information you enter is saved in your browser and used anonymously to improve the service — it's deleted after 30 days unless you sign in.",
        },

        kiitos: {
            title: "Thank you!",
            openBody: "Quote request {id} received — we'll forward it to pumping companies in your area. Replies will arrive at your email {email}.",
            noSupplyBody: "Quote request {id} received. There's no pumping company in this area yet, but you'll get a notification at your email {email} as soon as the service expands to your area.",
            jerryMessage: "You'll get an email notification as soon as the first offer arrives.",
            followOffers: "Track offers",
        },

        jerryPill: {
            imgAlt: "Jerry — your guide to the pumping service",
            greeting: "Hi, Jerry here.",
            defaultMessage: "I'll help you along.",
        },

        kayttoIkaToggle: {
            defaultBadge: "DEFAULT",
        },

        sectionHeader: {
            defaultEditLabel: "Edit",
        },

        specGuideModal: {
            title: "Assistant for choosing concrete",
            intro: "The right concrete depends on the conditions the structure is exposed to — whether it's indoors or outdoors, dry or damp, exposed to frost or sheltered. Answer a few questions about the structure and the assistant will work out a suitable exposure class and the matching strength class for your order.",
            presetCaption: "Pre-filled from the application:",
            selectKayttokohdeFirst: "Select an application before opening the assistant.",
            applyThese: "Use these",
        },

        specGuideResult: {
            recommendedTitle: "Recommended concrete",
            minLujuusLabel: "Minimum strength class",
            coverManual: "Per structural drawings",
            coverLabel: "Concrete cover",
        },

        rasitusluokkaField: {
            helperDefault: "Select the class that matches your application, or use the assistant above",
        },

        laskutusSection: {
            title: "Billing",
            switchLabel: "Change",
            billedToLabel: "Billed to",
            selectLabel: "Select who to bill — a company or individual",
            selectHelper: "Billing goes to the customer you select once the order is accepted.",
            typeCompany: " · Company",
            typePrivate: " · Private",
            privateOption: "Bill as a private individual{name}",
            createNew: "+ Create a new company…",
            alreadyExists: "The company is already on your list — selected for you.",
            ytunnusInvalid: "Enter the business ID in the format 1234567-8",
            nameRequired: "Enter the company name",
            nameTooLong: "The name is too long (max 250 characters)",
            createFailed: "Failed to create the company",
            ytunnusLabel: "Business ID",
            companyNameLabel: "Company name",
            saving: "Saving…",
            save: "Save",
        },

        attachmentsSection: {
            title: "Attachments",
            addLabel: "Add",
            helper: "e.g. pump setup spot, pour location, or formwork — you can add attachments later too",
            readyHint: "Attachments are optional.",
            altFallback: "Attachment",
            deleteAria: "Delete attachment",
            deleteFailed: "Failed to delete the attachment",
            takePhoto: "Take a photo",
            chooseImages: "Choose images",
            addPdf: "Add PDF",
        },

        attachmentsUpload: {
            noFileSelected: "No file selected",
            unsupportedType: "Only JPG, PNG, WEBP, or PDF supported",
            imageTooLarge: "The file is too large (max 50 MB)",
            fileTooLarge: "The file is too large (max 20 MB)",
            sasFetchFailed: "Failed to fetch the SAS token",
            sasEmpty: "SAS token empty",
            azureUploadFailed: "Failed to upload the file to Azure",
        },

        rasitusluokka: {
            groups: {
                common: "Most common",
                special: "Special cases",
                other: "Other",
            },
            presets: {
                unknown: { label: "I don't know / choose based on the application" },
                x0: { label: "X0 — Unreinforced interior", meaning: "No reinforcement and no environmental exposure — the simplest fill concrete.", example: "Foundation base fill, light fill under a floor." },
                xc1: { label: "XC1 — Ordinary interior, reinforced", meaning: "Normal room air, no moisture or substances that corrode the reinforcement.", example: "Walls, floor slabs, and intermediate floors in living spaces." },
                xc2: { label: "XC2 — Foundation wall / footing (below ground)", meaning: "The concrete is continuously in damp soil but can't freeze.", example: "Footings, basement walls, piles." },
                xc34xf1: { label: "XC3-4, XF1 — Weather-resistant (vertical surface)", meaning: "Normal outdoor air and rain, but water drains off the surface quickly.", example: "Building foundation wall, exterior walls, outdoor columns." },
                xc34xf3: { label: "XC3-4, XF3 — Weather-resistant horizontal surface (balcony slab)", meaning: "The concrete is horizontal, so water and snow can sit on the surface.", example: "Balcony slabs, outdoor stairs, yard paving." },
                xc3Suojattu: { label: "XC3 — Rain-sheltered exterior structure", meaning: "A variably damp outdoor space, but water doesn't reach the structure directly.", example: "A carport/canopy, porch, or a damp interior space such as a bathroom." },
                xc4Altis: { label: "XC4 — Rain-exposed exterior structure", meaning: "A variably wet exterior structure, but with no frost exposure.", example: "An unprotected exterior wall in warm climates (southern Europe)." },
                xd2: { label: "XD2 — Wet chloride exposure indoors", meaning: "An interior space where the reinforced structure is exposed to constant water splash and chlorides.", example: "Swimming halls, damp industrial spaces, pool structures." },
                xc34xd1xf2: { label: "XC3-4, XD1, XF2 — Mild frost-chloride exposure", meaning: "The concrete is occasionally exposed to salty water (e.g. salt dripping off a car).", example: "A home garage, a driveway that's lightly salted." },
                xc34xd2xf2: { label: "XC3-4, XD2, XF2 — Parking slab", meaning: "The floor of a parking area or garage — regular chloride exposure from car tires.", example: "A housing company's parking garage, a yard parking area." },
                xc34xd3xf4: { label: "XC3-4, XD3, XF4 — Severe frost-chloride exposure", meaning: "The concrete is constantly exposed to road salt and large amounts of water that freezes.", example: "Parking garages, road bridges, highway curbstones." },
                xc34xs1xf2: { label: "XC3-4, XS1, XF2 — Coastal house (sea-air climate)", meaning: "The structure is near the sea (under 1 km) and salt travels in as mist or airborne spray.", example: "The facade or foundation wall of a house located on the shore." },
                xc34xf4xs3: { label: "XC3-4, XF4, XS3 — On the shore (splash zone)", meaning: "The concrete is in direct contact with seawater or wave splash.", example: "Piers/jetties, breakwaters, a shore retaining wall." },
                xc2xs2: { label: "XC2, XS2 — Submerged in seawater", meaning: "The structure is continuously submerged in seawater, with no frost exposure.", example: "Seabed piles, a basement wall submerged in seawater." },
                xa2xc2: { label: "XA2, XC2 — Chemically aggressive exposure", meaning: "The soil or groundwater is chemically aggressive (e.g. sulfates).", example: "Footings on an industrial site, wastewater treatment plant structures." },
                muu: { label: "Other (specify in the description)" },
            },
            tree: {
                sisaUlko: {
                    question: "Is this an interior or exterior structure?",
                    options: {
                        sisa: { label: "Interior structure", description: "A heated interior space such as a living room, storage room, or laundry room." },
                        ulko: { label: "Exterior structure", description: "A structure outdoors or unheated. E.g. a foundation wall, balcony, yard, or cold storage." },
                    },
                },
                raudoitettu: {
                    question: "Is the structure reinforced?",
                    options: {
                        ei: { label: "No", description: "Plain mass concrete with no steel reinforcement. E.g. foundation base fill, a shallow footing slab." },
                        kylla: { label: "Yes", description: "Contains steel bars or mesh. Nearly all structural concrete (slabs, walls, columns, beams)." },
                    },
                },
                kosteustila: {
                    question: "What's the moisture condition?",
                    options: {
                        kuivaNormaaliSisa: { label: "Dry / ordinary interior", description: "A normal heated interior space. Living rooms, offices, storage rooms, insulated ground slabs in heated buildings, ordinary waterproofed bathrooms (the waterproofing keeps the concrete dry)." },
                        vaativaKostea: { label: "Demanding damp space", description: "A space that's continuously or often damp, where the concrete structure is exposed to moisture without waterproofing. E.g. a swimming hall, sauna, laundry room, commercial kitchen, cattle barn, industrial wet space." },
                        kloridit: { label: "Chlorides", description: "Salt + moisture. E.g. a garage floor (road-salt water), a parking garage, a ramp, a road bridge, shore structures, a swimming hall." },
                    },
                },
                pakkasrasitus: {
                    question: "Is there frost exposure?",
                    options: {
                        ei: { label: "No", description: "The structure stays below ground, in water, in a warm space, or sheltered from rain — it can't freeze while wet." },
                        kylla: { label: "Yes", description: "The structure gets wet and freezes alternately outdoors. Nearly all Finnish exterior structures (balcony, foundation wall, yard)." },
                    },
                },
                maanAllaTaiVedessa: {
                    question: "Is the structure below ground or in water?",
                    options: {
                        kylla: { label: "Yes", description: "Footings, basement exterior walls, piles, dams, a structure in groundwater." },
                        ei: { label: "No", description: "An above-ground exterior structure such as a foundation wall, exterior wall, balcony, or yard." },
                    },
                },
                sateeltaSuojattu: {
                    question: "Is the structure sheltered from rain?",
                    options: {
                        kylla: { label: "Yes", description: "Under an eave or canopy, no direct rain. E.g. a porch, a column under a canopy, outdoor stairs under a roof." },
                        ei: { label: "No", description: "A structure exposed to direct rain. E.g. a balcony, yard, unprotected exterior wall." },
                    },
                },
                kloridirasitusUlko: {
                    question: "Is there also chloride exposure?",
                    help: "Chloride means salt — road salt or seawater. Salt corrodes the reinforcement significantly faster than water alone.",
                    options: {
                        ei: { label: "No", description: "Just rain and frost, no salt. E.g. an ordinary exterior wall, a foundation wall far from a road, a balcony." },
                        kylla: { label: "Yes", description: "The structure gets salt from road salt or seawater. E.g. a garage, parking garage, road bridge, coastal house." },
                    },
                },
                pystyVaaka: {
                    question: "Vertical or horizontal structure?",
                    options: {
                        pysty: { label: "Vertical", description: "A vertical surface — water drains off quickly. E.g. a wall, foundation wall, column, railing." },
                        vaaka: { label: "Horizontal", description: "A horizontal surface — water and snow sit on the surface. E.g. a balcony, outdoor stairs, a yard slab." },
                    },
                },
                kloridiLahde: {
                    question: "What's the source of the chloride?",
                    options: {
                        jaansulatussuola: { label: "De-icing salt", description: "Road salt: sodium chloride used in winter. Melt water splashes onto structures from car tires or the roadside." },
                        merivesi: { label: "Seawater", description: "Salt water or a marine climate. Salt travels in as airborne mist (under 1 km from the shore) or hits directly (a pier, breakwater)." },
                    },
                },
                xdVoimakkuus: {
                    question: "Intensity of the frost and chloride exposure?",
                    options: {
                        lieva: { label: "Mild / occasional", description: "Salt occurs rarely or only via the air. E.g. a home garage floor (melt water dripping off tires in winter)." },
                        ankara: { label: "Severe / direct", description: "Salt occurs continuously or as a direct spray. E.g. a parking garage, road bridge, ramp, or immediate roadside." },
                    },
                },
                xsSijainti: {
                    question: "Location on the coast?",
                    options: {
                        ilmaSuola: { label: "Airborne salt", description: "On the coast but out of reach of waves. E.g. the facade of a house located on the shore (under 1 km)." },
                        roiskealue: { label: "Splash zone", description: "In direct contact with water or wave splash. E.g. a pier, breakwater, shore retaining wall." },
                        vedenAlla: { label: "Continuously underwater", description: "A permanently submerged part, not the splash or tidal zone. E.g. seabed piles, a basement wall submerged in seawater. Cover requires a structural engineer's check." },
                    },
                },
            },
            terminals: {
                x0: { resultLabel: "X0 — No corrosion or attack risk", description: "No risk" },
                xc1: { resultLabel: "XC1 — Ordinary interior, reinforced", description: "Living spaces" },
                xc3Sisa: { resultLabel: "XC3 — Damp interior", description: "Wet rooms, damp" },
                xd2: { resultLabel: "XD2 — Wet chloride exposure", description: "Swimming halls, etc." },
                xc2: { resultLabel: "XC2 — Foundation wall / footing (below ground)", description: "Footings, foundations" },
                xc3u: { resultLabel: "XC3 — Rain-sheltered exterior structure", description: "Outdoor, covered space" },
                xc4: { resultLabel: "XC4 — Rain-exposed exterior structure", description: "Unprotected vertical" },
                xf1: { resultLabel: "XC3-4, XF1 — Weather-resistant (vertical surface)", description: "Foundation wall, wall" },
                xf3: { resultLabel: "XC3-4, XF3 — Weather-resistant horizontal surface", description: "Balcony, stairs" },
                xd1: { resultLabel: "XC3-4, XD1, XF2 — Mild frost-chloride exposure (garage)", description: "Garage" },
                xd3: { resultLabel: "XC3-4, XD3, XF4 — Severe frost-chloride exposure", description: "Bridge, parking garage" },
                xs1: { resultLabel: "XC3-4, XS1, XF2 — Coastal house (sea-air climate)", description: "Coastal house" },
                xs2: { resultLabel: "XC2, XS2 — Submerged in seawater", description: "Seabed piles" },
                xs3: { resultLabel: "XC3-4, XS3, XF4 — On the shore (splash zone)", description: "Pier" },
            },
        },

        kayttokohdePresets: {
            anturat: {
                question: "Will the footing or part of it be exposed to weather/frost?",
                eiSaalleAltisLabel: "No, normally below ground",
                kyllaSaalleAltisLabel: "Yes, shallow or partly weather-exposed",
                kyllaSaalleAltisDescription: "Tightened to exposure class XF1 (vertical surface, frost exposure).",
            },
            lattia: {
                question: "Will the slab be exposed to de-icing salt (e.g. a garage)?",
                eiLabel: "No",
                kyllaLabel: "Yes",
                kyllaDescription: "We'll select the stricter XD1 class and a mix that's easier to finish.",
            },
        },

        quoteConstants: {
            betoniLaatuDisclaimer: "This is an estimate for ordering purposes. If the site has structural drawings, always use the concrete grade specified in them.",
            stepLabels: {
                osoite: "Address",
                pumppu: "Pump",
                betoni: "Concrete",
                lisatiedot: "Details",
                tarkista: "Review",
            },
            options: {
                kayttokohde: {
                    anturat: {
                        label: "Footings",
                        description: "Building foundation footings. The designer may alternatively accept XC2, or a combination of XC1 and XC2.",
                    },
                    sokkeli: {
                        label: "Foundation wall",
                        description: "Foundation wall and the base of a ground-bearing exterior wall — rain and frost exposure.",
                    },
                    harkko: {
                        label: "Block wall",
                        description: "Block-wall fill concrete — a flowable mix pumped into the block cavities.",
                    },
                    lattia: {
                        label: "Floor or slab",
                        description: "A ground-bearing slab or an interior floor. The garage case is asked about separately in the assistant.",
                    },
                    seinat: {
                        label: "Walls",
                        description: "Interior load-bearing or semi-load-bearing walls.",
                    },
                    holvi: {
                        label: "Vault / intermediate floor",
                        description: "The load-bearing slab of a roof deck or an intermediate floor.",
                    },
                    pilarit: {
                        label: "Columns",
                        description: "Interior posts and load-bearing columns.",
                    },
                    portaat: {
                        label: "Stairs",
                        description: "Interior or exterior concrete stairs. The assistant asks about the location.",
                    },
                    muu: {
                        label: "Other",
                        description: "Another application — specify it in the description field.",
                    },
                },
                lujuusluokka: {
                    unknown: "I don't know",
                    muu: "Other",
                },
                raekoko: {
                    unknown: "I don't know",
                    hieno16: "16 mm fine",
                    muu: "Other",
                },
                notkeus: {
                    unknown: "I don't know",
                    muu: "Other",
                },
                kayttoIka: {
                    v50: "50y",
                    v100: "100y",
                },
            },
        },
    },
    attachments: {
        uploadFailed: "Upload failed.",
    },
    jerry: {
        applyFailed: "Failed to send.",
    },

    landing: {
        hero: {
            microcopy: {
                fast: "Fast",
                free: "Completely free",
                noCommitment: "No commitment",
            },
            taglineLead: "Concrete pumping: ",
            taglineAccent: "one request, multiple offers.",
            taglineSub: "Concrete and pumping in one request — no need to call around.",
            mascotAlt: "Jerry, concrete pump operator",
            greeting: "Welcome, {name}.",
        },
        process: {
            heading: "How it works",
            subheading: "Three simple steps to offers and pumping day",
            steps: {
                step1: { title: "Send a free quote request", body: "Tell us about your site: address, concrete, timing, and boom needs. Takes under 2 minutes." },
                step2: { title: "Compare offers", body: "Pumping companies contact you with their offers. Compare prices and schedules." },
                step3: { title: "Choose the best and book the pumping", body: "Accept the right offer directly with the pumping company. No brokerage fees, no commitment." },
            },
        },
        leadGen: {
            heading: "Why Betonijerry?",
            saveTime: { title: "Save time", body: "One request reaches multiple pumping companies. You don't have to call around — they send you offers." },
            network: { title: "Wide coverage" },
            trusted: { title: "Trusted pumping companies", body: "The pumping companies on our list are professionals with the necessary licenses and insurance." },
        },
        requestForm: {
            pageTitle: "Request a Concrete Pumping Quote — BetoniJerry",
            pageDescription: "Send one quote request and compare multiple offers for concrete pumping. Free and non-binding — takes under 2 minutes.",
            waitingForAttachments: "Wait for the attachments to finish uploading.",
            confirmBillingToContinue: "Confirm your billing selection to continue.",
            floorPlanSnapshotComment: "Floor plan from the concrete calculator",
        },
        notFound: {
            pageTitle: "Page Not Found — BetoniJerry",
            heading: "Oops — we couldn't find this page. The link may be outdated or incorrect.",
            cta: "Back to home",
        },
        cookieConsent: {
            ariaLabel: "Cookie settings",
            intro: "Betonijerry does not use cookies for its own operation.",
            body: "However, we use Google advertising and visitor analytics, which may store cookies with your consent. {link}",
            readMore: "Learn more",
            reject: "Reject",
            accept: "Accept",
        },
    },

    valut: {
        shared: {
            newRequestCta: "Request a new quote →",
        },
        status: {
            draft: "Draft",
            open: "Open",
            pendingVerification: "Under review",
            accepted: "Accepted",
            cancelled: "Cancelled",
            expired: "Expired",
            noSupply: "Not in your area",
        },
        myValutPage: {
            pageTitle: "My Pours — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            intro: "Your sent quote requests and the offers you've received for them.",
            tabs: {
                avoimet: "Open",
                hyvaksytyt: "Accepted",
                peruuntuneet: "Cancelled",
                eiAlueellasi: "Not in your area yet",
            },
            noSupplyNotice: "You'll get an email when the service expands to your area.",
            loadingAriaLabel: "Loading quote requests…",
            loadFailed: "Failed to load quote requests. Please try again.",
            emptyOpen: "No open quote requests. Request a new quote — replies will appear here.",
            emptyAccepted: "No accepted pours yet.",
            emptyCancelled: "No cancelled requests.",
            emptyNoSupply: "No requests from areas that don't have providers yet.",
        },
        listCard: {
            mapAlt: "Map image of the address {address}",
            mapAltUnavailable: "Map image not available",
            sentPrefix: "Sent {date}",
            acceptedPrefix: "Accepted: {provider}",
            offerCountLabel: "{count} offers",
            boomFromRear: "Pumped from the rear",
            boomSuffix: "{m} m boom",
            linjaSuffix: "{m} m line",
            viewCta: "View →",
        },
        detail: {
            pageTitle: "Pour request: {address} — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            pushToastDefaultTitle: "A new offer has arrived",
            notFound: "Quote request not found.",
            backToMyValut: "← Back to my pours",
            recapHeading: "Summary of your request",
            addressLabel: "Address",
            kayttokohdeLabel: "Application",
            amountLabel: "Amount",
            rasitusluokkaLabel: "Exposure class",
            boomLabel: "Boom",
            boomFromRear: "Pumped from the rear",
            linjaLabel: "Line",
            driveLabel: "Directions",
            detailsLabel: "Additional details",
            offersHeading: "Offers ({count})",
            validUntil: "This quote request is valid until {date}",
            waitingForOffers: "We're waiting for offers — you'll get an email as soon as the first offer arrives.",
            subscribePromptLabel: "Notify my browser when an offer arrives",
            subscribeSuccessLabel: "Notifications on for this device",
            subscribeHelperText: "You'll get a browser notification as soon as a pumping company sends an offer.",
            viewersHeading: "These companies have viewed your request ({count})",
            viewersHelper: "A company may contact you directly or submit an offer in this service.",
            declinedHeading: "Pumping companies that declined ({count})",
            declinedHelper: "These companies won't be submitting an offer for this request.",
        },
        offerCard: {
            showLess: "Show less",
            readMoreIntro: "More…",
            priceEstimateLabel: "Price estimate",
            priceAriaLabel: "Price estimate {total}, includes {vat} percent value added tax",
            vatLine: "VAT {vat}% incl. · 0% VAT: {netto}",
            unitPriceLine: "≈ {unitPrice} (estimate, 0% VAT)",
            priceTermsLabel: "What's included in the price",
            locationLabel: "Location",
            locationValue: "{dist} from the site",
            validUntilLabel: "Valid until",
            validUntilValue: "{date}",
            cancellationLabel: "Cancellation terms",
            cancellationHint: "Read more below",
            readMoreExpand: "▾ Read more about this offer",
            readMoreCollapse: "▴ Close",
            extraNotesLabel: "Additional details",
            askViaChat: "Ask via messages",
            chatTitle: "Messages · {name}",
            acceptedNotice: "This offer was accepted on {date}",
        },
        acceptConfirmModal: {
            providerFieldValue: "{name} (Business ID {ytunnus})",
            totalWithVat: "{total} (incl. {vat}% VAT)",
            validUntilValue: "{date}",
            cancel: "Cancel",
        },
        acceptedScreen: {
            heading: "Congratulations, you've chosen a pumping company!",
            contactNamed: "{name} will contact you to confirm the pumping time.",
            contactGeneric: "The pumping company will contact you to confirm the pumping time.",
            contactPersonLabel: "Contact person: {name}",
            phoneLabel: "Phone:",
            hoursLabel: "Opening hours: {hours}",
            backToMyValut: "Back to my pours",
        },
        progress: {
            step1Title: "You'll get offers",
            step1Sub: "Compare prices and schedules at your own pace",
            step2Title: "Choose a pumping company",
            step2Sub: "contacting them doesn't commit you to anything",
            step3Title: "Confirm the pumping time",
            step3Sub: "details are agreed by phone after acceptance",
        },
    },

    laskuri: {
        page: {
            pageTitle: "Concrete Calculator for Floor Pours — Calculate Concrete Usage (m³) | BetoniJerry",
            pageDescription: "A free visual concrete calculator for floor pours: draw a floor plan, set the slab thickness and drain slopes — get an exact concrete amount (m³) and an order recommendation.",
            calculatorLoadFailed: "The calculator failed to load. Refresh the page and try again.",
            heading: "Concrete Calculator for Floor Pours",
            intro: "Draw your floor's plan, set the slab thickness and drain slopes — the calculator gives you the exact concrete amount and a recommended order quantity. Free, no sign-in required.",
            useInQuoteCta: "Request offers with this amount",
            howComposedHeading: "What makes up the concrete usage?",
            howComposedBody1: "The concrete amount for a floor pour is area times thickness — but in reality the slab thickness varies, wet rooms have slopes to floor drains, and the floor plan is rarely a rectangle. This calculator computes the volume directly from the floor plan you draw: areas can overlap (overlapping area isn't counted twice), thickness can be set per corner, and drain slopes are subtracted automatically.",
            howComposedBody2: "Once you know the amount, you can request concrete pumping offers using the same floor plan — one request reaches the pumping companies in your area.",
            faqHeading: "Frequently asked questions",
            faq: {
                demand: { q: "How much concrete does a floor pour need?", a: "The need is area times slab thickness. For example a 20 m² floor with a 100 mm slab is 2.0 m³. The calculator also accounts for slopes, varying thicknesses and irregular shapes, and adds a waste allowance." },
                wasteAllowance: { q: "What is the waste allowance and why is it needed?", a: "The base is never perfectly level, so more concrete is always used than the calculation suggests. A typical waste allowance is 5–10%. Running out mid-pour is costly — better to order slightly more than to run short." },
                drainSlope: { q: "How does the slope to a floor drain affect the concrete amount?", a: "The slope thins the slab around the drain, so slightly less concrete is used. The calculator automatically subtracts the slope's volume when you add a drain and set its depth and radius." },
            },
        },
        calculator: {
            useAmountDefault: "Use amount",
        },
        toolbar: {
            addRoomRect: "Add room (rectangle)",
            addRoomTri: "Add room (triangle)",
            addRoomCircle: "Add room (circle)",
            addPillar: "Add column",
            addDrain: "Add floor drain",
            presets: "Templates",
            fitView: "Fit view",
            undo: "Undo",
            clearAll: "Clear all",
            viewToggleAriaLabel: "View (2D / 3D)",
            view2dAriaLabel: "2D view",
            view3dAriaLabel: "3D view",
            exitFullscreenTooltip: "Exit fullscreen (Esc)",
            exitFullscreenAriaLabel: "Exit fullscreen",
            fullscreen: "Fullscreen",
        },
        drainPanel: {
            heading: "Floor drain and slope",
            close: "Close",
            depthLabel: "Slope depth",
            radiusLabel: "Slope radius",
            ratioHint: "Slope ≈ 1:{ratio} — the usual recommendation for wet rooms is 1:80–1:100, steeper right next to the drain.",
            outsidePourWarning: "The drain is not on the poured area — it doesn't affect the amount.",
            exceedsThicknessWarning: "The slope exceeds the slab thickness ({mm} mm) — there won't be enough concrete at the bottom of the slope.",
            deleteDrain: "Delete drain",
        },
        shapePanel: {
            close: "Close",
            cornerLabelsRect: { nw: "Top left", ne: "Top right", se: "Bottom right", sw: "Bottom left" },
            cornerLabelsTri: { c1: "Corner 1", c2: "Corner 2", c3: "Corner 3" },
            typeLabels: { rect: "Rectangle", tri: "Triangle", circle: "Circle", pillar: "Column" },
            widthLabel: "Width",
            lengthLabel: "Length",
            diameterLabel: "Diameter",
            heightLabel: "Height",
            uniformThickness: "Same thickness",
            perCornerThickness: "Per corner",
            thicknessLabel: "Thickness",
            duplicate: "Duplicate",
            delete: "Delete",
            circleThicknessNote: "A circle always has the same thickness across its whole area.",
            pillarNote: "A column is calculated as a cylinder and added to the concrete amount. It's not included in the floor area.",
        },
        resultPanel: {
            areaLabel: "Area",
            exactLabel: "Concrete",
            recommendedLabel: "Recommendation",
            decreaseWaste: "Decrease waste allowance",
            wasteAllowanceLabel: "Waste allowance {pct}%",
            increaseWaste: "Increase waste allowance",
            computeError: "Calculation failed — showing the last successful result.",
        },
        // Used inline by planModel.js's floorPlanSummary() — a pure module with no
        // bundle access, so the caller resolves this word via t() and passes it in.
        floorPlanSummary: {
            recommendedWord: "recommended",
        },
        presetPicker: {
            title: "Start from a template",
            emptyPlan: "Blank plan",
            presets: {
                autotalli: { name: "Garage", description: "Single-car garage 6.0 × 3.6 m, 100 mm slab" },
                kylpyhuone: { name: "Bathroom + sauna", description: "Washroom and sauna, 80 mm slab, slopes to drains" },
                terassi: { name: "Terrace slab", description: "Beveled terrace, 80 mm slab" },
            },
        },
        plan3dView: {
            noWebgl: "Your browser doesn't support the 3D view (WebGL is missing). Use the 2D view.",
        },
        planCanvas: {
            zoomIn: "Zoom in",
            zoomOut: "Zoom out",
        },
    },

    legal: {
        shared: {
            versionLabel: "Version {version}",
            effectiveDateSuffix: " · effective {date}",
        },
        kayttoehdot: {
            pageTitle: "Terms of Service — BetoniJerry",
            pageDescription: "BetoniJerry's terms of service. BetoniJerry is PumiNet Oy's quote-request service for concrete pumping.",
            heading: "Terms of Service",
            loadFailed: "Failed to load the terms of service. Try refreshing the page in a moment.",
        },
        tietosuoja: {
            pageTitle: "Privacy Policy — BetoniJerry",
            pageDescription: "BetoniJerry's privacy policy — how your personal data is processed. Data controller: PumiNet Oy.",
            heading: "Privacy Policy",
            loadFailed: "Failed to load the privacy policy. Try refreshing the page in a moment.",
        },
        gate1Fallback: {
            checkboxLabel:
                "I agree that Betonijerry shares my quote request and contact details with selected concrete pumping companies so they can send offers. " +
                "I confirm that the site details I've provided are correct to the best of my knowledge. " +
                "I understand that sending the quote request does not yet commit me to ordering the service.",
            details: {
                marketplace: "Betonijerry is a quote-request and marketplace service. Betonijerry does not perform concrete pumping itself and does not sell concrete. Any service agreement is always formed between you and the provider you choose.",
                accuracy: "You are responsible for the address, amount, concrete, and site details you provide being accurate, and for the site having safe access and a suitable setup spot for the pump. The provider may revise the offer or decline the job if the conditions differ significantly from what was reported.",
                leftoverAmount: "The concrete amount ordered is always larger than the amount pumped: part of every load stays in the pumping equipment (boom and line) and doesn't reach the pour, and the amount is rounded up in 0.5 m³ steps. Leftover concrete is the customer's responsibility — it stays on-site or is taken for recycling for a separate fee.",
                noStructuralResponsibility: "Betonijerry is not responsible for structural design, the technical suitability of the concrete grade, or the choice of exposure class or strength class. If the site has structural drawings, follow the instructions of the designer or site supervisor.",
                dataProcessing: "We process the quote request details and your contact information to forward offers and provide customer service. Further details on how we process personal data are described separately in our privacy policy.",
                contactVisibility: "The pumping companies who receive your request can see your contact details (name, address, phone, email) once they've signed in to the service, and may contact you directly. You'll get an email notification when a company views your request.",
                offerOrdering: "The visibility and order of offers may be based on factors such as location, equipment suitability, availability, and response time.",
            },
        },
        peruutusehdotStandard: {
            summary: "Free cancellation until 12:00 on the previous working day. For an order not cancelled in time, the provider charges €240.",
            full:
                "Cancellation of a pump order must be made by 12:00 on the previous working day at the latest. "
                + "For an order not cancelled in time, the provider charges €240. "
                + "If admixtures had already been ordered before the pump order was cancelled, the provider will also charge for those admixtures. "
                + "The cancellation fee is a contractual term of the provider you selected; Betonijerry is not a party to the charge. "
                + "This does not limit a consumer's rights under the Consumer Protection Act.",
        },
        gate2Fallback: {
            ctaLabel: "ACCEPT OFFER",
            ctaCaption: "The binding order and pumping time are agreed by phone after acceptance",
            ariaLabel: "Accept offer, the binding order and pumping time are agreed by phone after acceptance",
            confirmTitle: "Confirm selection",
            responsibilityLabel: "Customer's responsibility",
            responsibilityBody: "The binding order and pumping time are agreed by phone after acceptance.",
            fieldLabels: {
                provider: "Provider",
                total: "Total price",
                cancellation: "Cancellation terms",
                validUntil: "Valid until",
            },
        },
    },

    // /yhteystiedot. Names, addresses and numbers are NOT here — they are
    // constants in the page component (identifiers, not translatable copy).
    contact: {
        pageTitle: "Contact — BetoniJerry",
        pageDescription:
            "BetoniJerry contact details: named contact person, phone and email. The service is operated by {operator} (business ID {businessId}).",
        heading: "Contact",
        lead: "Questions about a quote request, an offer you received, or the service itself? Call or send an email — we answer on weekdays.",
        personLabel: "Contact person",
        phoneLabel: "Phone",
        emailLabel: "Email",
        generalLabel: "General enquiries",
        requestHint: "If your message concerns a quote request you already sent, include the site address and the pour date — we'll find the request faster.",
        privacyLabel: "Privacy / data protection",
        operatorLabel: "Operator",
        operatorValue: "{operator} · Business ID {businessId}",
        operatorNote: "BetoniJerry forwards quote requests to concrete pumping companies in your area. BetoniJerry does not perform the pumping itself and does not sell concrete.",
    },

    // Task 19 — provider-facing pages. Binding-terminology rule: a provider's
    // submitted price is a BINDING offer (never "estimate"/"rough price"/
    // "indication"/"no obligation"/"ballpark"); tarjouspyyntö = "quote request",
    // tarjous (what a provider sends) = "offer", never the same word twice for
    // one referent in one string.
    provider: {
        shared: {
            cancel: "Cancel",
            edit: "Edit",
            declineCta: "Decline this request",
            priceLabel: "Price (€)",
            priceHelper: "Price excluding VAT — 25.5% VAT is added to the customer's invoice",
            extraNotesLabel: "Additional details (optional)",
            extraNotesHelper: "E.g. concrete grade, delivery, working hours",
            companyType: "Company",
            privateType: "Private individual",
        },
        detail: {
            pageTitle: "Quote request: {address} — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            providersOnlyNotice: "Quote requests are for pumping companies.",
            notFound: "Quote request not found.",
            backToRequests: "← Back to quote requests",
            backToList: "← Quote requests",
            loadFailed: "Failed to load. Try again.",
            requestIdLabel: "Quote request #{id}",
            confirmScheduleCta: "Confirm and schedule in betoni.online →",
            wonNotice: "The customer accepted your offer!",
            yourOfferHeading: "Your offer",
            declinedNotice: "You declined this quote request.",
            declinedReasonSuffix: " Reason: {reason}",
            undeclineCta: "Undo decline and make an offer",
            endedNoOfferNotice: "This quote request has ended.",
            contactSupportCta: "Contact support",
            chatHeading: "Customer messaging (chat)",
            chatPreOfferHint: "You can ask the customer for clarification before making an offer.",
            askCustomerCta: "Ask the customer",
            supportDialogTitle: "Support",
            supportIntro: "Write your message to support. It won't be sent until you press Send.",
            supportPlaceholder: "Describe what you need help with…",
            sendToSupportCta: "Send to support",
            supportThreadOpenFailed: "Couldn't open the support conversation. Try again.",
            supportContactFailed: "Failed to contact support. Try again.",
            declineDialogIntro: "We'll let the customer know you won't be making an offer. You can optionally give a reason (visible to the customer).",
            declineReasonPlaceholder: "E.g. equipment booked for that day (optional)",
            confirmDeclineCta: "Confirm: decline",
            declineFailed: "Failed to decline. Try again.",
            withdrawCta: "Withdraw offer",
            withdrawDialogIntro: "The offer is removed from the customer's choices and moves to Ended. Withdrawing can't be undone.",
            confirmWithdrawCta: "Confirm: withdraw offer",
            withdrawFailed: "Failed to withdraw the offer. Try again.",
        },
        preview: {
            deniedText: "You don't have access to this quote request yet. Contact your company's admin or BetoniJerry support.",
            deniedSignedInAs: "You are signed in as {email}. If your company already has a BetoniJerry or betoni.online account, sign in with that one.",
            switchAccountCta: "Sign in with another account",
            unavailableText: "This quote request is no longer available to your company — it may have been re-sent to other providers.",
            pageTitle: "Quote request preview — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            invalidLinkNotice: "This link isn't valid. Check the latest message in your email or log in.",
            invalidLinkAuthenticatedNotice: "This link isn't valid. Check the latest message in your email or open your quote requests in the app.",
            loginCta: "Log in",
            openRequestsCta: "Open quote requests",
            expiredAuthenticatedNotice: "This link has expired, but you can open the quote request while logged in.",
            expiredAnonNotice: "This link has expired. Log in to see the quote request.",
            openRequestCta: "Open quote request",
            loadingText: "Loading the quote request…",
            heading: "Quote request #{id}",
            summaryAmount: "{m3} m³",
            summaryDuration: "{h} h",
            attachmentsRevealedHint: "The customer's attachments are visible once you're logged in.",
            revealCta: "Register interest",
            revealHint: "We'll let the customer know your company is interested. No commitment.",
            interestSentNotice: "Thanks — the customer has been told you're interested.",
            genericRetryError: "Something went wrong. Try again in a moment.",
            requestEndedNotice: "This quote request has ended.",
            alreadyOfferedNotice: "Your company has already made an offer for this quote request.",
            loginViewOfferCta: "Log in and view your offer",
            viewOfferCta: "View your offer",
            declinedNotice: "Your company has declined this quote request.",
            makeOfferHeading: "Make an offer",
            commitmentNotice: "The price is a binding offer. The customer's contact details appear right after sending, and the order is agreed by phone.",
            sendOfferCta: "Send offer",
            confirmOfferQuestion: "Send a binding offer of €{price} (excl. VAT)?",
            confirmSendCta: "Yes, send the offer",
            confirmEditCta: "Edit the price",
            offerSentNotice: "Offer sent. The customer's phone number and email now appear with the request.",
            priceRequiredError: "Enter a price before sending the offer.",
            cannotBidWithLinkNotice: "An offer can be sent from this link only if the invitation went to a personal email address.",
            loginToOfferCta: "Log in and make an offer",
            openInAppCta: "Open in the app",
            loginOpenInAppCta: "Log in and open in the app",
            openInAppHint: "In the app you can add attachments and message the customer.",
            declineHeading: "Not offering?",
            declineReasonLabel: "Tell us briefly why",
            declineReasonHelper: "Shown to the customer so they know what happened.",
            declineSubmitCta: "Send decline",
            declineCancelCta: "Cancel",
            declineSentNotice: "Thanks — the customer has been told you're not offering.",
            declineReasonRequiredError: "Please write a short reason.",
        },
        offerForm: {
            priceTermsLabel: "Price terms",
            extraOptionsLabel: "More options",
            validUntilLabel: "Offer valid until",
            attachmentsLabel: "Attachments",
            pendingNotice: "Your offer has been sent to the customer. You can still update the price and attachments until the customer accepts it.",
            draftNotice: "This is a draft. The customer won't see it until you press \"Send to customer\".",
            lockedNotice: "The offer, price and attachments are visible only to you and the customer. Other pumping companies can't see them.",
            // Shown directly under the price field in EVERY state. Pricing is
            // treated as confidential in this trade, so the guarantee belongs
            // where the hesitation happens, not at the foot of the form.
            privacyNotice: "Your price is shown only to the customer. Other pumping companies never see it.",
            notSentYetNotice: "The customer sees your offer only once you send it.",
            updateDraftCta: "Update draft",
            updateOfferCta: "Update offer",
            saveDraftCta: "Save draft",
            sendCta: "Send to customer",
        },
        offerAttachments: {
            uploadFailed: "Upload failed: {files}",
            selectFilesCta: "Choose files",
            disabledHint: "Save a draft first to add attachments",
            acceptedTypesHint: "JPG / PNG / WEBP / PDF",
            removeAria: "Remove {filename}",
        },
        list: {
            providersOnlyNotice: "Quote requests are for pumping companies.",
            pageTitle: "Quote requests — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            intro: "Open quote requests and the offers you've made.",
            loadFailed: "Failed to load quote requests. Try again.",
            emptyAvoimet: "No open quote requests right now.",
            emptyTarjotut: "You haven't sent any offers for open requests.",
            emptyVoitetut: "No won offers yet.",
            emptyPaattyneet: "No ended quote requests.",
        },
        tuki: {
            pageTitle: "Support conversations — Betonijerry",
            pageTitleFallback: "Betonijerry — Request a Concrete Pumping Quote",
            heading: "Support conversations",
            intro: "Your requests to our support team.",
            tabOpen: "Open",
            tabResolved: "Resolved",
            tabAll: "All",
            loadFailed: "Failed to load support conversations. Try again.",
            emptyNotice: "No support conversations.",
            emptyOpenHint: "If you need to, open a conversation with support from a quote request page, or {link}.",
            emptyOpenHintLink: "send an email",
            dialogTitleOffer: "Offer #{id} — support",
            dialogTitleGeneric: "Support conversation",
            cardTitleOffer: "Offer #{id}",
            cardTitleGeneric: "Conversation #{id}",
            statusResolved: "Resolved",
            statusOpen: "Open",
            openOfferCta: "Open offer →",
        },
        people: {
            heading: "People",
            firstNameLabel: "First name",
            lastNameLabel: "Last name",
            emailLabel: "Email",
            phoneLabel: "Phone",
            memoLabel: "Comment",
            roleAdminLabel: "Administrator (admin)",
            roleAdminChip: "Admin",
            roleTarjousLabel: "Offer rights",
            roleTarjousChip: "Offer",
            rolePumppariLabel: "Pump operator",
            validateOneFieldRequired: "Fill in at least one field",
            editDialogTitle: "Edit person",
            addDialogTitle: "Add person",
            globalVisibilityLabel: "Global visibility:",
            globalChip: "Global",
            companySpecificChip: "Company-specific",
            makeGlobalCta: "Make global",
            makeGlobalDialogTitle: "Make global?",
            makeGlobalDialogBody: "The person will become visible to all companies in the system. Only professional contact details (name, email, phone) are shared.",
        },
        vehicles: {
            heading: "Vehicles",
            nameLabel: "Name",
            regNoLabel: "Registration number",
            boomSortLabel: "Boom",
            boomFieldLabel: "Boom (m)",
            vehicleNoLabel: "Vehicle number",
            typeLabel: "Vehicle type",
            memoLabel: "Additional details",
            gpsTrackingLabel: "GPS tracking installed",
            noGpsLabel: "No GPS tracking",
            showInactiveLabel: "Also show deactivated",
            reactivateFailed: "Failed to reactivate",
            deactivateAria: "Deactivate",
            deactivateConfirmTitle: "Deactivate this vehicle?",
            deactivateConfirmBody: "The vehicle will be removed from the active list but stays in historical records. You can reactivate it later.",
            deactivateFailLabel: "Failed to deactivate",
            reactivateAria: "Reactivate",
            defaultVehicleName: "Vehicle",
            deletedChip: "Deactivated",
            editDialogTitle: "Edit vehicle",
            addDialogTitle: "Add vehicle",
        },
        sijainnit: {
            heading: "Locations",
            nameLabel: "Name",
            typeLabel: "Type",
            streetLabel: "Street address",
            postalCityLabel: "Postal code and city",
            jerryActiveLabel: "Jerry enabled",
            jerryInactiveLabel: "Jerry not enabled",
            jerryActiveHelp: "This location receives BetoniJerry quote requests from its area.",
            deliveryDistanceLabel: "Delivery distance (km)",
            deliveryDistanceHelp: "How far pumping jobs are driven from this location (as the crow flies).",
            puomiMinLabel: "Boom min (m)",
            // Mirrors the fi.js fb#415 correction — boom min does NOT filter
            // anything. Never describe it as a matching bound.
            puomiMinHelp: "Does not filter quote requests — any pump can be run as a line pump without the boom. Enter your longest boom in the Boom max field.",
            puomiMaxLabel: "Boom max (m)",
            puomiMaxHelp: "The longest boom operating from this location. A quote request is routed here only when the requested boom length fits within it. An empty field means no limit.",
            puomiRangeError: "Boom min cannot be greater than boom max",
            editDialogTitle: "Edit location",
            addDialogTitle: "Add location",
        },
        liity: {
            areaRequiredError: "Tell us your service area",
            phoneRequiredError: "Phone number is required",
            coverageInvalidError: "Enter the coverage range in kilometers",
            doneAlert: "Thank you! Your application has been received — we'll be in touch soon.",
            doneHint: "Jerry membership is activated manually. You'll be notified once your company is added.",
            heading: "Join BetoniJerry",
            introPrefixWithCompany: "{company} is not yet a Jerry provider. ",
            introPrefixNoCompany: "Your company is not yet a Jerry provider. ",
            introBody: "With Jerry you get concrete pumping quote requests straight from your area. Fill in a short application — we'll review it and be in touch.",
            alreadyPendingNotice: "Your application is already being processed. You can update the details and resend it if needed.",
            areaFieldLabel: "Service area",
            areaFieldHelper: "E.g. Helsinki, Uusimaa",
            coverageFieldLabel: "Coverage range (km)",
            coverageFieldHelper: "How far you deliver",
            phoneFieldLabel: "Phone",
            noteFieldLabel: "Message (optional)",
            submitting: "Sending…",
            submitCta: "Send application",
        },
        recap: {
            heading: "Request",
            mapAlt: "Map image of the address {address}",
            guidedQuestionsCount: "The customer answered these questions ({count})",
            manualSpecNotice: "The customer chose the concrete grade themselves.",
            addressLabel: "Address",
            customerLabel: "Customer",
            phoneLabel: "Phone",
            emailLabel: "Email",
            kayttokohdeLabel: "Application",
            amountLabel: "Amount",
            floorPlanLabel: "Floor plan",
            durationLabel: "Duration (estimate)",
            boomLabel: "Boom",
            lineLabel: "Line",
            concreteDescLabel: "Concrete description",
            driveInstructionsLabel: "Directions",
            notesLabel: "Additional details",
            customerAttachmentsHeading: "Customer's attachments",
            respondByPrefix: "Respond by {date} · ",
            footerLine: "#{id} · Received {date}",
        },
        listCard: {
            pumpBoomSuffix: "{m} m boom",
            pumpLineSuffix: "{m} m line",
            sitePhotoAlt: "Site photo",
            mapAltRevealed: "Map image of the address {address}",
            mapAltMasked: "Map image of the area {address}",
            noImageAlt: "No image",
            receivedPrefix: "Received {date}",
            viewCta: "View →",
            distanceTooltip: "As the crow flies from the nearest depot",
        },
        entityList: {
            filter: "Filter",
            add: "Add",
            searchPlaceholder: "Search…",
            clearSearchAria: "Clear search",
            sortByLabel: "Sort by:",
            sortDefault: "Default",
            changeSortDirAria: "Change sort direction",
            loadFailed: "Failed to load",
            deleteLabel: "Delete",
            confirmDeleteTitle: "Delete this?",
            confirmDeleteBody: "This action cannot be undone.",
            deleteFailed: "Failed to delete",
            noSearchResults: "No search results.",
            noData: "No data yet.",
        },
        entityFormDialog: {
            saveFailed: "Failed to save",
            saveLabel: "Save",
            savingLabel: "Saving…",
        },
        entitySchema: {
            requiredError: "{label} is required",
            invalidError: "{label} is invalid",
            invalidEmail: "Email address is invalid",
        },
        companyBasics: {
            ytunnusPrefix: "Business ID: {value}",
        },
        hallinta: {
            heading: "Management",
        },
        hallintaApi: {
            vehicleCreateFailed: "vehicle/new did not return a vehicleId",
        },
    },
};
